// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { Breakpoint, SxProps, Theme } from './index';

/**
 * Appends and overwrites styles with the first defined style in lowest priority
 * If the style evaluates to falsey, it is not appended
 */
export const sxAppend = (
    ...styles: Array<SxProps<Theme> | false | undefined | null | 0 | ''>
): SxProps<Theme> =>
    styles.reduce(
        (previousStyles: SxProps<Theme>, newStyle) => ({
            ...previousStyles,
            ...(newStyle || {}),
        }),
        {}
    );

/** The idea for this came from https://stackoverflow.com/questions/68567118/react-material-ui-typography-max-number-of-lines
 * Documentation for the `-webkit-line-clamp` property can be found {@link https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp here}.
 * {@link https://caniuse.com/?search=webkit-box Here} is a list of browsers that support the `-webkit-box` display property.
 * We took this approach because it is supported by various browsers, but if a better idea comes to light, then we definitely should revisit this.
 */
export const styleMaxLines = (
    maxLines?: number | Record<Breakpoint, number>
): SxProps => ({
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxLines,
});

export const styleNoScrollbar: SxProps = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
};

export const styleColorOverlay = (
    backgroundColor: string,
    overlayColor: string
): { background: string } => ({
    background: `linear-gradient(0deg, ${overlayColor} 0%, ${overlayColor} 100%), ${backgroundColor}`,
});

export const styleArrowBackIosCorrector = {
    position: 'relative',
};

/** Either a number or a breakpoint object with numbers */
export type ValueByBreakpoint<T = string | number> = T | Record<Breakpoint, T>;
export type NumberByBreakpoint = ValueByBreakpoint<number>;

export function getValueByBreakpoint<T = string | number>(
    value: T | ValueByBreakpoint<T>,
    breakpoint: Breakpoint
): T | undefined {
    if (typeof value === 'string' || typeof value === 'number') {
        return value as T;
    }
    return (value as Record<Breakpoint, T>)[breakpoint];
}

/** Default padding for page layouts */
export const responsivePaddingDefault = (theme: Theme) => ({
    xs: theme.ds.spacingUnits.SIDE_SMALL,
    sm: theme.ds.spacingUnits.SIDE_SMALL,
    md: theme.ds.spacingUnits.SIDE_MEDIUM,
    lg: theme.ds.spacingUnits.SIDE_LARGE,
});

/** Default icon size */
export const styleIconMedium = {
    width: '35px',
    height: '35px',
};

export const styleIconLarge = {
    width: '40px',
    height: '40px',
};

export const styleIconContainer = {
    borderRadius: '50%',
    width: '64px',
    height: '64px',
    p: 3,
    '& > svg': styleIconLarge,
};

export const enum MaskOrientation {
    Top = 0,
    Right = 90,
    Bottom = 180,
    Left = 270,
}

/** Full-viewport wrapper with gradient background and grid layout for header/content/footer */
export const styleContentWrapper = {
    height: '100dvh',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

/** Gradient background style for loading/transition screens */
export const styleLoadingScreen = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

/**
 * Creates a gradient mask style that transitions from transparent to opaque along a specified orientation.
 *
 * @param orientation - Direction of the gradient mask (Top, Right, Bottom, Left)
 * @param maskProportion - Controls where the gradient reaches full opacity.
 *   Range: 0-1, where 0.5 (default) means the gradient reaches full opacity at 50% of the distance.
 *   Lower values create a sharper transition, higher values create a softer, more gradual fade.
 * @param maskStartProportion - Controls where the gradient starts transitioning from transparent.
 *   Range: 0-1, where 0 (default) means the gradient starts at the very beginning.
 *   Higher values move the start point further along the element.
 * @param symmetric - When true, mirrors the fade onto the opposite end too, so both ends
 *   fade to transparent and only the middle stays opaque (default: false, fades one end only).
 *
 * @returns SxProps object with maskImage property containing a linear gradient
 *
 * @example
 * // Create a top-to-bottom mask that fades to full opacity at 60%
 * styleMaskedContainer(MaskOrientation.Top, 0.6, 0)
 *
 * @example
 * // Create a left-to-right mask starting at 20% and reaching full opacity at 70%
 * styleMaskedContainer(MaskOrientation.Left, 0.7, 0.2)
 *
 * @example
 * // Create a mask that fades in from both edges, opaque between 15% and 85%
 * styleMaskedContainer(MaskOrientation.Right, 0.85, 0, true)
 */
export function styleMaskedContainer(
    orientation: MaskOrientation,
    maskProportion = 0.5,
    maskStartProportion = 0,
    symmetric = false
): SxProps<Theme> {
    const maskPercentage = (1 - maskProportion) * 100;
    const maskStartPercentage = maskStartProportion * 100;
    const gradient = (transparent: string, opaque: string) =>
        symmetric
            ? `linear-gradient(${orientation}deg, ${transparent} ${maskStartPercentage}%, ${opaque} ${maskPercentage}%, ${opaque} ${
                  100 - maskPercentage
              }%, ${transparent} ${100 - maskStartPercentage}%)`
            : `linear-gradient(${orientation}deg, ${transparent} ${maskStartPercentage}%, ${opaque} ${maskPercentage}%)`;
    return {
        maskImage: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? gradient(
                      'rgba(255, 255, 255, 0)',
                      DS_COLOR.LIGHT_BACKGROUND_PAPER
                  )
                : gradient(
                      'rgba(26, 26, 44, 0)',
                      DS_COLOR.DARK_BACKGROUND_PAPER
                  ),
    };
}
